import React, { useState, useEffect } from 'react';
import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Switch,
  Card,
  CardContent,
} from '@mui/material';
import {
  SmartToy, // AI Assistant icon
  Settings, // Manual configuration icon
  Upload, // File upload icon
  Close,
  Send,
  Help,
  People,
  Schedule,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { ssoAPI, entraAPI, usersAPI, oktaAPI } from '../services/api';
import BrandedWordmark from './BrandedWordmark';
import { CreateSSOConfigurationRequest, SSOPlatform, SAMLAttributeStatement } from '../types';
import auditService from '../services/auditService';
import { useAuth } from '../contexts/AuthContext';
import { API_ROOT } from '../config/apiConfig';
import { FORM_PLACEHOLDERS } from '../constants/formPlaceholders';
import { UI_TOKENS } from '../constants/uiTokens';
import { getRiskSurface } from '../utils/appearanceTokens';
import type { SSOPrefillData } from '../types';

export type { SSOPrefillData };

interface SSOOnboardingDialogProps {
  open: boolean;
  onClose: () => void;
  applicationId: string;
  applicationName: string;
  instanceId: string;
  riskLevel?: string;
  initialData?: SSOPrefillData;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sso-tabpanel-${index}`}
      aria-labelledby={`sso-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2.5 }}>{children}</Box>}
    </div>
  );
}

const SSOOnboardingDialog: React.FC<SSOOnboardingDialogProps> = ({
  open,
  onClose,
  applicationId,
  applicationName,
  instanceId,
  riskLevel,
  initialData,
}) => {
  const { user } = useAuth();
  const muiTheme = useMuiTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Read org-wide IDP platform from settings (set by super admin in Settings → General)
  const orgPlatform = (localStorage.getItem('turboiam-idp-platform') || 'azure') as SSOPlatform;

  // Security policy mapped to this app's risk level
  const [riskPolicy, setRiskPolicy] = useState<{ policy_name: string; policy_type: string } | null>(null);
  const [policyLookupCode, setPolicyLookupCode] = useState<string | null>(null);
  const [policyLookupMessage, setPolicyLookupMessage] = useState<string | null>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateSSOConfigurationRequest>({
    application_id: applicationId,
    sso_type: 'SAML',
    platform: orgPlatform,
    connection_name: '',
    attributes_to_release: [],
    grant_type: 'authorization_code_pkce',
    scopes: ['openid', 'profile', 'email'],
    
    // SAML fields
    entity_id: '',
    acs_url: '',
    name_id_format: '',
    x509_certificate: '',
    private_key: '',
    certificate_expiration_date: '',
    idp_initiated_url: '',
    metadata_url: '',
    
    // OIDC fields
    client_id: '',
    client_secret: '',
    redirect_uris: [],
    oidc_discovery_endpoint: '',
    client_secret_expires_at: '',
    
    // Azure specific fields
    azure_metadata_url: '',
    
    // Okta specific fields
    okta_app_id: '',
    okta_metadata_url: '',
    
    // Google specific fields
    google_project_id: '',
    google_client_id: '',
    google_consent_screen_configured: false,
    
    // New fields for enhanced workflow
    delegated_to: '',
    business_justification: '',
    expected_user_count: 0,
    data_classification: 'internal',
    technical_notes: '',
    security_requirements: {
      mfa_required: false,
      session_timeout: 30,
      ip_restrictions: [],
      allowed_domains: [],
      encryption_required: false,
      audit_logging_required: true,
      risk_assessment_score: 5,
    },
  });

  // Auto-fill contact email and sync org platform when dialog opens
  useEffect(() => {
    if (open) {
      const platform = localStorage.getItem('turboiam-idp-platform') || 'azure';
      setFormData(prev => ({
        ...prev,
        platform: platform as any,
        contact_email: prev.contact_email || user?.email || '',
        application_id: applicationId || prev.application_id,
      }));
    }
  }, [open, applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply AI-prefilled data when dialog opens
  useEffect(() => {
    if (open && initialData) {
      setFormData(prev => ({
        ...prev,
        ...(initialData.sso_type && { sso_type: initialData.sso_type }),
        ...(initialData.platform && { platform: initialData.platform as any }),
        ...(initialData.connection_name && { connection_name: initialData.connection_name }),
        ...(initialData.entity_id && { entity_id: initialData.entity_id }),
        ...(initialData.acs_url && { acs_url: initialData.acs_url }),
        ...(initialData.metadata_url && { metadata_url: initialData.metadata_url }),
        ...(initialData.name_id_format && { name_id_format: initialData.name_id_format }),
        ...(initialData.oidc_discovery_endpoint && { oidc_discovery_endpoint: initialData.oidc_discovery_endpoint }),
        ...(initialData.redirect_uris && { redirect_uris: initialData.redirect_uris }),
        ...(initialData.scopes && { scopes: initialData.scopes }),
        ...(initialData.grant_type && { grant_type: initialData.grant_type as any }),
        ...(initialData.contact_email && { contact_email: initialData.contact_email }),
        ...(initialData.business_justification && { business_justification: initialData.business_justification }),
      }));
      // Switch to the correct tab based on sso_type
      if (initialData.sso_type === 'OIDC') setTabValue(1);
      else if (initialData.sso_type === 'SAML') setTabValue(0);
    }
  }, [open, initialData]);

  // Attributes state
  const [availableAttributes, setAvailableAttributes] = useState<any[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  /**
   * Okta-style SAML attribute statements. Used when the platform is Okta + SSO type SAML.
   * Matches https://support.okta.com/help/s/article/how-to-add-attribute-statement-via-api
   */
  const [attributeStatements, setAttributeStatements] = useState<SAMLAttributeStatement[]>([]);
  const [oktaAttributesSource, setOktaAttributesSource] = useState<string | null>(null);
  const [loadingOktaAttributes, setLoadingOktaAttributes] = useState(false);
  const [samlFieldsFromMetadata, setSamlFieldsFromMetadata] = useState(false);

  const formatAttributeStatementLine = (stmt: SAMLAttributeStatement): string => {
    const values = (stmt.values || []).filter(Boolean).join(', ');
    if (stmt.type === 'GROUP') {
      return `${stmt.name} [GROUP] ${stmt.filterType || ''} ${stmt.filterValue || ''}`.trim();
    }
    return `${stmt.name} (${stmt.namespace || 'basic'}) = ${values}`;
  };

  const pullOktaSamlAttributes = async (oktaAppId?: string) => {
    setLoadingOktaAttributes(true);
    setError('');
    try {
      const res = await oktaAPI.fetchSamlAttributeStatements(oktaAppId?.trim() || undefined);
      const payload = res.data?.data;
      const list = (payload?.attribute_statements || []) as SAMLAttributeStatement[];
      setAttributeStatements(list);
      setOktaAttributesSource(payload?.source || 'okta');
      setSuccess(
        list.length > 0
          ? `Loaded ${list.length} SAML attribute mapping(s) from Okta.`
          : 'No attribute mappings returned from Okta.'
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Could not pull SAML attributes from Okta. Check Okta integration under Security.'
      );
    } finally {
      setLoadingOktaAttributes(false);
    }
  };

  // AI Assistant state
  const [aiMessages, setAiMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([
    {
      role: 'assistant',
      content: `Hello! I'm VYSOR, your SSO onboarding assistant. I'll help you configure ${applicationName} for SSO integration.

You can paste a **metadata URL** (e.g. ending in \`.xml\`) and I'll auto-fill the SAML form, or just describe what you need and I'll guide you.`
    }
  ]);
  const [aiInput, setAiInput] = useState('');
  const [aiConversationId, setAiConversationId] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedMetadata, setUploadedMetadata] = useState<string>('');
  const [metadataUrl, setMetadataUrl] = useState<string>('');
  const [fetchingMetadata, setFetchingMetadata] = useState(false);
  /** IdP metadata import summary shown on Manual tab (Issuer / SSO URL). */
  const [importedIdpMeta, setImportedIdpMeta] = useState<{
    entityId?: string;
    ssoLoginUrl?: string;
    nameIdFormat?: string;
    sourceUrl?: string;
  } | null>(null);

  // Template selection state
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [useTemplate, setUseTemplate] = useState(false);

  // Delegation state
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showDelegation, setShowDelegation] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Pull SAML attribute statements from Okta when Okta + SAML is selected
  useEffect(() => {
    if (!open) return;
    if (formData.sso_type !== 'SAML' || formData.platform !== 'okta') return;
    if (attributeStatements.length > 0 && oktaAttributesSource) return;
    void pullOktaSamlAttributes(formData.okta_app_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData.sso_type, formData.platform]);

  const dataClassifications = [
    { value: 'public', label: 'Public', description: 'No restrictions' },
    { value: 'internal', label: 'Internal', description: 'Company internal use only' },
    { value: 'confidential', label: 'Confidential', description: 'Sensitive business information' },
    { value: 'restricted', label: 'Restricted', description: 'Highly sensitive data' }
  ];

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!applicationId?.trim()) {
      newErrors.application_context =
        'Application is missing. Go back to VYSOR, confirm which app/instance to configure (use the IDs from your app list), or start SSO from Applications.';
    }
    if (!instanceId?.trim()) {
      newErrors.instance_context =
        'Environment instance is missing. VYSOR must include the instance UUID from your app list before you can submit.';
    }

    // Required fields validation
    if (!formData.connection_name?.trim()) {
      newErrors.connection_name = 'Connection name is required';
    }

    if (!formData.contact_email?.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else if (!validateEmail(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }


    if (formData.sso_type === 'SAML') {
      const hasMetadata =
        Boolean(formData.metadata_url?.trim()) ||
        Boolean(formData.okta_metadata_url?.trim()) ||
        Boolean(formData.azure_metadata_url?.trim()) ||
        Boolean(uploadedMetadata && uploadedMetadata.length > 40);

      if (!hasMetadata && !formData.entity_id?.trim()) {
        newErrors.entity_id = 'Entity ID is required for SAML';
      }

      if (!hasMetadata && !formData.acs_url?.trim()) {
        newErrors.acs_url = 'ACS URL is required for SAML';
      } else if (formData.acs_url?.trim() && !validateUrl(formData.acs_url)) {
        newErrors.acs_url = 'ACS URL must be a valid URL';
      }

      // Optional URL validations
      if (formData.metadata_url && !validateUrl(formData.metadata_url)) {
        newErrors.metadata_url = 'Metadata URL must be a valid URL';
      }


      if (formData.okta_metadata_url && !validateUrl(formData.okta_metadata_url)) {
        newErrors.okta_metadata_url = 'Okta metadata URL must be a valid URL';
      }

      if (formData.idp_initiated_url && !validateUrl(formData.idp_initiated_url)) {
        newErrors.idp_initiated_url = 'IDP initiated URL must be a valid URL';
      }
    }

    if (formData.sso_type === 'OIDC') {
      if (!formData.grant_type?.trim()) {
        newErrors.grant_type = 'Grant type is required for OIDC';
      }

      if ((formData.grant_type === 'authorization_code' || formData.grant_type === 'authorization_code_pkce')) {
        if (!formData.redirect_uris || formData.redirect_uris.length === 0) {
          newErrors.redirect_uris = 'Redirect URIs are required for this grant type';
        } else {
          // Validate each redirect URI
          const invalidUris = formData.redirect_uris.filter(uri => !validateUrl(uri.trim()));
          if (invalidUris.length > 0) {
            newErrors.redirect_uris = 'All redirect URIs must be valid URLs';
          }
        }
      }

      if (formData.oidc_discovery_endpoint && !validateUrl(formData.oidc_discovery_endpoint)) {
        newErrors.oidc_discovery_endpoint = 'OIDC discovery endpoint must be a valid URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormChange = (field: keyof CreateSSOConfigurationRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Real-time validation for specific fields
    if (field === 'contact_email' && value && !validateEmail(value)) {
      setErrors(prev => ({
        ...prev,
        [field]: 'Please enter a valid email address'
      }));
    } else if (field === 'acs_url' && value && !validateUrl(value)) {
      setErrors(prev => ({
        ...prev,
        [field]: 'ACS URL must be a valid URL'
      }));
    } else if (field === 'redirect_uris' && value && Array.isArray(value)) {
      const invalidUris = value.filter(uri => uri.trim() && !validateUrl(uri.trim()));
      if (invalidUris.length > 0) {
        setErrors(prev => ({
          ...prev,
          [field]: 'All redirect URIs must be valid URLs'
        }));
      }
    }
  };

  // Load available attributes and templates on component mount
  useEffect(() => {
    loadAvailableAttributes();
    loadTemplates();
    if (riskLevel && ['Low', 'Medium', 'High', 'Critical'].includes(riskLevel)) {
      loadRiskPolicy(riskLevel);
    }
  }, [riskLevel]);

  // Reload delegate picker when dialog opens (role users cannot call /auth/users).
  useEffect(() => {
    if (open) {
      void loadAvailableUsers();
    }
  }, [open]);

  const loadRiskPolicy = async (level: string) => {
    setLoadingPolicy(true);
    setRiskPolicy(null);
    setPolicyLookupCode(null);
    setPolicyLookupMessage(null);
    try {
      // Authoritative resolve (any authenticated enterprise user) — do NOT use
      // /policy-mappings list (RBAC-gated; 403 looked like "no policy").
      const res = await fetch(
        `${API_ROOT}/okta/policy-mappings/resolve?risk_level=${encodeURIComponent(level)}`,
        authFetchInit(),
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.code === 'POLICY_LOOKUP_ERROR') {
        setPolicyLookupCode('POLICY_LOOKUP_ERROR');
        setPolicyLookupMessage(
          data?.message || 'Could not look up the security policy. Try again or contact an admin.',
        );
        return;
      }
      setPolicyLookupCode(data?.code || null);
      setPolicyLookupMessage(data?.message || null);
      if (data?.code === 'POLICY_FOUND' && data?.policy) {
        setRiskPolicy({
          policy_name: data.policy.policy_name,
          policy_type: data.policy.policy_type || '',
        });
      } else {
        setRiskPolicy(null);
      }
    } catch {
      setPolicyLookupCode('POLICY_LOOKUP_ERROR');
      setPolicyLookupMessage('Could not look up the security policy. Try again or contact an admin.');
    } finally {
      setLoadingPolicy(false);
    }
  };

  // Load available users for delegation
  const loadAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await usersAPI.getUsersForPicker();
      setAvailableUsers(users);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };


  const loadAvailableAttributes = async () => {
    try {
      const res = await oktaAPI.listSsoAttributes('application');
      setAvailableAttributes(res.data?.data || []);
    } catch (error) {
      console.error('Error loading attributes:', error);
      setAvailableAttributes([]);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const templates = await entraAPI.getTemplates();
      setAvailableTemplates(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
      // Don't show error to user as templates are optional
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleConnectionNameChange = (value: string) => {
    // Remove spaces and special characters, keep only alphanumeric
    const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
    handleFormChange('connection_name', cleanValue);
  };

  const handleAttributeChange = (attributeId: string, checked: boolean) => {
    if (checked) {
      setSelectedAttributes(prev => [...prev, attributeId]);
    } else {
      setSelectedAttributes(prev => prev.filter(id => id !== attributeId));
    }
  };

  // Extract certificate expiration date from X.509 certificate
  const extractCertificateExpiration = (certString: string): string | null => {
    try {
      // Clean the certificate string (remove headers, footers, whitespace)
      const cleanCert = certString
        .replace(/-----BEGIN CERTIFICATE-----/g, '')
        .replace(/-----END CERTIFICATE-----/g, '')
        .replace(/\s/g, '');

      if (!cleanCert) {
        return null;
      }

      // Create X509Certificate object (this is a browser-compatible approach)
      // Note: In a real implementation, you might want to use a library like 'node-forge' or similar
      // For now, we'll return null and let the backend handle the extraction
      return null;
    } catch (error) {
      console.error('Error extracting certificate expiration:', error);
      return null;
    }
  };

  const handleAISubmit = async () => {
    if (!aiInput.trim()) return;

    const userMessage = { role: 'user' as const, content: aiInput };
    setAiMessages(prev => [...prev, userMessage]);
    const inputSnapshot = aiInput;
    setAiInput('');

    // Detect if the user pasted a metadata URL
    const urlMatch = inputSnapshot.match(/https?:\/\/\S+/i);
    const looksLikeMetadataUrl = urlMatch && (
      inputSnapshot.toLowerCase().includes('metadata') ||
      urlMatch[0].toLowerCase().includes('metadata') ||
      urlMatch[0].toLowerCase().includes('.xml')
    );

    if (looksLikeMetadataUrl && urlMatch) {
      const detectedUrl = urlMatch[0].replace(/[.,;)]+$/, ''); // strip trailing punctuation
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: `I see you've provided a metadata URL. Let me fetch and parse it for you...`
      }]);
      try {
        const response = await ssoAPI.fetchMetadataFromURL(detectedUrl);
        const metadataContent: string = response.data?.data?.content || '';
        if (metadataContent.includes('EntityDescriptor')) {
          setUploadedMetadata(metadataContent);
          parseSAMLMetadataIntoForm(metadataContent, detectedUrl);
          setAiMessages(prev => [...prev, {
            role: 'assistant',
            content: `I've successfully parsed the SAML metadata from ${detectedUrl} and filled in the form fields on the right:\n\n` +
              `• **SSO Type:** SAML\n` +
              `• **Entity ID:** ${metadataContent.match(/entityID="([^"]+)"/i)?.[1] || '(extracted)'}\n` +
              `• **ACS URL:** ${(metadataContent.match(/AssertionConsumerService[^>]*Binding="[^"]*HTTP-POST[^"]*"[^>]*Location="([^"]+)"/i) || metadataContent.match(/AssertionConsumerService[^>]*Location="([^"]+)"/i))?.[1] || '(extracted)'}\n` +
              `• **Certificate:** ${metadataContent.includes('X509Certificate') ? 'Extracted ✓' : 'Not found'}\n\n` +
              `Switch to the **Manual Configuration** tab to review and complete any remaining fields before submitting.`
          }]);
        } else {
          setAiMessages(prev => [...prev, {
            role: 'assistant',
            content: `I fetched the URL but it doesn't appear to be a SAML EntityDescriptor. Please check the URL and try again, or paste the metadata XML directly.`
          }]);
        }
      } catch (err: any) {
        setAiMessages(prev => [...prev, {
          role: 'assistant',
          content: `I wasn't able to fetch the metadata from that URL (${err.response?.data?.error || err.message}). You can try pasting the XML content directly, or use the metadata URL field in the Manual Configuration tab.`
        }]);
      }
      return;
    }

    // Call real AI backend
    setAiLoading(true);
    try {
      const contextPrefix = !aiConversationId
        ? `[Context: Configuring SSO for application "${applicationName}" (ID: ${applicationId})] `
        : '';
      const res = await fetch(
        `${API_ROOT}/assistant/chat`,
        {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            message: contextPrefix + inputSnapshot,
            conversationId: aiConversationId,
          }),
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'AI error');
      if (data.conversationId) setAiConversationId(data.conversationId);
      const d = data.data;
      setAiMessages(prev => [...prev, { role: 'assistant', content: d.message || 'Done.' }]);
      if (d.prefill) {
        if (d.prefill.sso_type) handleFormChange('sso_type', d.prefill.sso_type);
        if (d.prefill.entity_id) handleFormChange('entity_id', d.prefill.entity_id);
        if (d.prefill.acs_url) handleFormChange('acs_url', d.prefill.acs_url);
        if (d.prefill.metadata_url) handleFormChange('metadata_url', d.prefill.metadata_url);
        if (d.prefill.connection_name) handleFormChange('connection_name', d.prefill.connection_name);
        if (d.prefill.contact_email) handleFormChange('contact_email', d.prefill.contact_email);
      }
    } catch (err: any) {
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I ran into an error (${err.message}). You can still configure SSO manually using the **Manual** tab.`,
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/xml' && file.type !== 'application/xml') {
      setError('Please select an XML file');
      return;
    }

    setSelectedFile(file);
    setLoading(true);

    try {
      const response = await ssoAPI.uploadMetadata(file);
      const result = response.data.data;
      setUploadedMetadata(result.content);
      setSuccess('Metadata file uploaded successfully!');
      
      // Auto-fill form with extracted data (simplified for now)
      if (result.content.includes('EntityDescriptor')) {
        parseSAMLMetadataIntoForm(result.content, undefined);
      } else if (result.content.includes('openid_configuration')) {
        handleFormChange('sso_type', 'OIDC');
      }
    } catch (err) {
      setError('Failed to upload metadata file');
    } finally {
      setLoading(false);
    }
  };

  // Parse SAML metadata XML content and fill the form fields
  const parseSAMLMetadataIntoForm = (metadataContent: string, sourceUrl?: string) => {
    const xml = String(metadataContent || '');
    const hasTag = (name: string) => new RegExp(`<(?:[\\w]+:)?${name}\\b`, 'i').test(xml);
    const isIdp = hasTag('IDPSSODescriptor') && !hasTag('SPSSODescriptor') && !hasTag('AssertionConsumerService');
    const isSp = hasTag('SPSSODescriptor') || hasTag('AssertionConsumerService');

    const entityId =
      xml.match(/\bentityID\s*=\s*"([^"]+)"/i)?.[1] ||
      xml.match(/\bentityID\s*=\s*'([^']+)'/i)?.[1];

    const certRaw =
      xml.match(/<(?:[\w]+:)?X509Certificate[^>]*>\s*([\s\S]+?)\s*<\/(?:[\w]+:)?X509Certificate>/i)?.[1];
    const certPem = certRaw
      ? `-----BEGIN CERTIFICATE-----\n${certRaw.replace(/\s+/g, '')}\n-----END CERTIFICATE-----`
      : undefined;

    const nameIdFormat =
      xml.match(/<(?:[\w]+:)?NameIDFormat[^>]*>\s*([^<\s]+)\s*<\/(?:[\w]+:)?NameIDFormat>/i)?.[1]?.trim();

    const ssoLocations: Array<{ binding?: string; location: string }> = [];
    const ssoRe = /<(?:[\w]+:)?SingleSignOnService\b([^>]*)\/?>/gi;
    let ssoMatch: RegExpExecArray | null;
    while ((ssoMatch = ssoRe.exec(xml)) !== null) {
      const attrs = ssoMatch[1] || '';
      const location =
        attrs.match(/Location\s*=\s*"([^"]+)"/i)?.[1] ||
        attrs.match(/Location\s*=\s*'([^']+)'/i)?.[1];
      if (!location) continue;
      const binding =
        attrs.match(/Binding\s*=\s*"([^"]+)"/i)?.[1] ||
        attrs.match(/Binding\s*=\s*'([^']+)'/i)?.[1];
      ssoLocations.push({ binding, location });
    }
    const ssoLoginUrl =
      ssoLocations.find(s => /HTTP-POST/i.test(s.binding || ''))?.location ||
      ssoLocations.find(s => /HTTP-Redirect/i.test(s.binding || ''))?.location ||
      ssoLocations[0]?.location;

    const acsPostMatch = isSp
      ? (
        xml.match(
          /<(?:[\w]+:)?AssertionConsumerService[^>]*Binding="[^"]*HTTP-POST[^"]*"[^>]*Location="([^"]+)"/i,
        ) ||
        xml.match(/<(?:[\w]+:)?AssertionConsumerService[^>]*Location="([^"]+)"/i)
      )
      : null;

    const urlLower = String(sourceUrl || '').toLowerCase();
    const looksOkta = /okta\.com/i.test(urlLower) || /okta\.com/i.test(xml);
    const looksAzure =
      /login\.microsoftonline\.com|sts\.windows\.net|microsoftonline/i.test(urlLower) ||
      /login\.microsoftonline\.com|sts\.windows\.net/i.test(xml);

    const oktaAppId =
      sourceUrl?.match(/\/app\/(exk[a-zA-Z0-9]+)\//i)?.[1] ||
      entityId?.match(/okta\.com\/(exk[a-zA-Z0-9]+)/i)?.[1] ||
      undefined;

    const connFallback = applicationName.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
    const displayName =
      xml.match(/<(?:[\w]+:)?OrganizationDisplayName[^>]*>([^<]+)<\/(?:[\w]+:)?OrganizationDisplayName>/i)?.[1]?.trim() ||
      xml.match(/<(?:[\w]+:)?OrganizationName[^>]*>([^<]+)<\/(?:[\w]+:)?OrganizationName>/i)?.[1]?.trim();

    setFormData(prev => {
      const next = { ...prev, sso_type: 'SAML' as const };
      if (sourceUrl) next.metadata_url = sourceUrl;
      if (certPem) next.x509_certificate = certPem;
      if (nameIdFormat) next.name_id_format = nameIdFormat;
      if (ssoLoginUrl) next.idp_initiated_url = ssoLoginUrl;
      if (!prev.connection_name) next.connection_name = displayName || connFallback;
      if (!prev.contact_email && user?.email) next.contact_email = user.email;

      if (looksOkta) {
        next.platform = 'okta';
        if (sourceUrl) next.okta_metadata_url = sourceUrl;
        if (oktaAppId) next.okta_app_id = oktaAppId;
      } else if (looksAzure) {
        next.platform = 'azure';
        if (sourceUrl) next.azure_metadata_url = sourceUrl;
      }

      if (isSp) {
        if (entityId) next.entity_id = entityId;
        if (acsPostMatch?.[1]) next.acs_url = acsPostMatch[1];
      }
      // IdP metadata: do not copy IdP issuer into SP Entity ID / ACS
      return next;
    });

    if (isIdp) {
      setImportedIdpMeta({
        entityId: entityId || undefined,
        ssoLoginUrl: ssoLoginUrl || undefined,
        nameIdFormat: nameIdFormat || undefined,
        sourceUrl: sourceUrl || undefined,
      });
      setSamlFieldsFromMetadata(false);
    } else {
      setImportedIdpMeta(null);
      setSamlFieldsFromMetadata(Boolean(acsPostMatch && entityId));
    }

    // Jump to Manual so imported values are visible
    setTabValue(1);
    try {
      if (looksOkta) localStorage.setItem('turboiam-idp-platform', 'okta');
      else if (looksAzure) localStorage.setItem('turboiam-idp-platform', 'azure');
    } catch {
      /* ignore */
    }
  };

  const handleFetchMetadataFromURL = async () => {
    if (!metadataUrl.trim()) {
      setError('Please enter a metadata URL');
      return;
    }

    setFetchingMetadata(true);
    setError('');

    try {
      // Use backend proxy to avoid CORS issues
      const response = await ssoAPI.fetchMetadataFromURL(metadataUrl);
      const metadataContent: string = response.data?.data?.content || '';
      if (!metadataContent) throw new Error('Empty response from metadata URL');

      setUploadedMetadata(metadataContent);

      if (metadataContent.includes('EntityDescriptor')) {
        parseSAMLMetadataIntoForm(metadataContent, metadataUrl.trim());
        const hasIdp = /<(?:[\w]+:)?IDPSSODescriptor\b/i.test(metadataContent);
        const hasAcs = /<(?:[\w]+:)?AssertionConsumerService\b/i.test(metadataContent);
        const isIdp = hasIdp && !hasAcs;
        setSuccess(
          isIdp
            ? 'IdP metadata imported — check Manual for Issuer, SSO URL, NameID, certificate, and Okta fields. ACS is the app Reply URL (not in this file); enter it if you have it, or submit with the metadata URL.'
            : 'Metadata imported — Entity ID, ACS URL, and certificate have been filled in on Manual.',
        );
      } else if (metadataContent.includes('openid_configuration')) {
        handleFormChange('sso_type', 'OIDC');
        setSuccess('OIDC discovery document fetched.');
        setTabValue(1);
      } else {
        setSuccess('Metadata fetched — please verify the extracted values.');
      }
    } catch (err: any) {
      console.error('Error fetching metadata from URL:', err);
      setError(`Failed to fetch metadata: ${err.response?.data?.error || err.message}`);
    } finally {
      setFetchingMetadata(false);
    }
  };

  const handleSubmit = async () => {
    // Validate form before submission
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Log SSO submission event
      if (user) {
        await auditService.logSSOSubmission(
          applicationId,
          applicationName,
          user.id,
          `${user.first_name} ${user.last_name}`,
          user.role,
          {
            business_justification: formData.business_justification,
            expected_user_count: formData.expected_user_count,
            data_classification: formData.data_classification,
            delegated_to: formData.delegated_to,
            technical_notes: formData.technical_notes,
            sso_type: formData.sso_type,
            platform: formData.platform,
          }
        );
      }

      // Note: Azure AD application creation is now handled by the backend after approval
      // The frontend only submits the SSO configuration request

      // Clean up the attribute-statement rows: drop empty rows, default namespace + type, and
      // make sure value lists are non-empty (fall back to user.<name> so Okta accepts it).
      const cleanedAttributeStatements: SAMLAttributeStatement[] = (attributeStatements || [])
        .map((stmt) => {
          const name = (stmt.name || '').trim();
          const values = (stmt.values || [])
            .map((v) => (typeof v === 'string' ? v.trim() : ''))
            .filter((v) => v.length > 0);
          return {
            type: stmt.type === 'GROUP' ? 'GROUP' : 'EXPRESSION',
            name,
            namespace:
              stmt.namespace || 'urn:oasis:names:tc:SAML:2.0:attrname-format:unspecified',
            values: values.length > 0 ? values : name ? [`user.${name}`] : [],
            filterType: stmt.type === 'GROUP' ? stmt.filterType ?? null : null,
            filterValue: stmt.type === 'GROUP' ? stmt.filterValue ?? null : null,
          } as SAMLAttributeStatement;
        })
        .filter((stmt) => stmt.name.length > 0 && stmt.values.length > 0);

      // Client Credentials without a client ID → SSO team creates Entra App Registration after approve
      const requestEntraOnApprove =
        formData.sso_type === 'OIDC' &&
        formData.grant_type === 'client_credentials' &&
        !String(formData.client_id || '').trim();

      // Prepare submission data
      const submissionData: CreateSSOConfigurationRequest = {
        ...formData,
        application_id: applicationId,
        instance_id: instanceId,
        attributes_to_release: selectedAttributes,
        attribute_statements: cleanedAttributeStatements.length > 0 ? cleanedAttributeStatements : undefined,
        ai_conversation: tabValue === 0 ? aiMessages : undefined,
        manual_config: (() => {
          const base = tabValue === 1 ? { ...formData } : {};
          if (requestEntraOnApprove) {
            return {
              ...base,
              request_entra_create_on_approve: true,
              request_entra_app_create: true,
              grant_type: formData.grant_type,
            };
          }
          return tabValue === 1 ? formData : undefined;
        })(),
        metadata_xml: uploadedMetadata || undefined,
        metadata_url: formData.metadata_url || metadataUrl || undefined,
      };

      await ssoAPI.createConfiguration(submissionData);
      
      // Log SSO creation event
      if (user) {
        await auditService.logSSOCreation(
          applicationId,
          applicationName,
          user.id,
          `${user.first_name} ${user.last_name}`,
          user.role,
          {
            sso_type: formData.sso_type,
            platform: formData.platform,
            connection_name: formData.connection_name,
            entity_id: formData.entity_id,
            acs_url: formData.acs_url,
            grant_type: formData.grant_type,
            redirect_uris: formData.redirect_uris,
            scopes: formData.scopes,
          }
        );
      }
      
      setSuccess('SSO configuration submitted successfully!');
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
        setSuccess('');
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting SSO configuration:', err);
      
      // Provide more detailed error information
      if (err.response?.data?.error) {
        setError(`Failed to submit SSO configuration: ${err.response.data.error}`);
      } else if (err.message) {
        setError(`Failed to submit SSO configuration: ${err.message}`);
      } else {
        setError('Failed to submit SSO configuration. Please check the console for details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSAMLTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {importedIdpMeta && (
        <>
          {sectionLabel('Imported from IdP metadata')}
          <Alert severity="info" sx={{ py: 0.75, fontSize: UI_TOKENS.text.buttonSm }}>
            This file is IdP federation metadata. App Entity ID and ACS (Reply URL) are not inside it —
            enter those below if you have them, or submit using the metadata URL alone.
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              label="IdP Entity ID / Issuer"
              value={importedIdpMeta.entityId || ''}
              InputProps={{ readOnly: true }}
            />
            <TextField
              size="small"
              fullWidth
              label="IdP SSO URL"
              value={importedIdpMeta.ssoLoginUrl || formData.idp_initiated_url || ''}
              InputProps={{ readOnly: true }}
            />
            <TextField
              size="small"
              fullWidth
              label="Metadata URL"
              value={formData.metadata_url || formData.okta_metadata_url || importedIdpMeta.sourceUrl || ''}
              InputProps={{ readOnly: true }}
            />
            <TextField
              size="small"
              fullWidth
              label="Signing certificate"
              value={formData.x509_certificate ? 'Imported ✓' : 'Not found'}
              InputProps={{ readOnly: true }}
            />
          </Box>
        </>
      )}
      {sectionLabel('SAML Configuration (application / SP)')}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <TextField size="small" fullWidth required={!formData.metadata_url && !formData.okta_metadata_url} label="Entity ID" value={formData.entity_id || ''}
          onChange={(e) => handleFormChange('entity_id', e.target.value)}
          placeholder={FORM_PLACEHOLDERS.samlEntityId} error={!!errors.entity_id}
          helperText={
            samlFieldsFromMetadata
              ? 'Imported from metadata (read-only)'
              : importedIdpMeta
                ? 'App/SP Entity ID (Audience URI) — not the IdP Issuer above'
                : errors.entity_id || undefined
          }
          InputProps={{ readOnly: samlFieldsFromMetadata }} />
        <TextField size="small" fullWidth required={!formData.metadata_url && !formData.okta_metadata_url} label="ACS URL" value={formData.acs_url || ''}
          onChange={(e) => handleFormChange('acs_url', e.target.value)}
          placeholder={FORM_PLACEHOLDERS.samlAcsUrl} error={!!errors.acs_url}
          helperText={
            samlFieldsFromMetadata
              ? 'Imported from metadata (read-only)'
              : importedIdpMeta
                ? 'App Reply URL / ACS — not present in IdP metadata'
                : errors.acs_url || undefined
          }
          InputProps={{ readOnly: samlFieldsFromMetadata }} />
        <TextField size="small" fullWidth label="Name ID Format" value={formData.name_id_format || ''}
          onChange={(e) => handleFormChange('name_id_format', e.target.value)}
          placeholder={FORM_PLACEHOLDERS.samlNameIdFormat}
          error={!!errors.name_id_format}
          helperText={samlFieldsFromMetadata ? 'Imported from metadata (read-only)' : errors.name_id_format || undefined}
          InputProps={{ readOnly: samlFieldsFromMetadata }} />
      </Box>
      {formData.platform === 'okta' && (
        <>
          {sectionLabel('Okta')}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField size="small" fullWidth label="Okta App ID" value={formData.okta_app_id || ''}
              onChange={(e) => handleFormChange('okta_app_id', e.target.value)}
              onBlur={() => {
                if (formData.okta_app_id?.trim()) {
                  void pullOktaSamlAttributes(formData.okta_app_id);
                }
              }}
              placeholder="0oa1b2c3d4e5f6g7h8i9" error={!!errors.okta_app_id}
              helperText="Optional — enter to load attribute mappings from that SAML app in Okta" />
            <TextField size="small" fullWidth label="Okta Metadata URL" value={formData.okta_metadata_url || ''}
              onChange={(e) => handleFormChange('okta_metadata_url', e.target.value)}
              placeholder={FORM_PLACEHOLDERS.oktaMetadataUrl} error={!!errors.okta_metadata_url} />
          </Box>
        </>
      )}
      {formData.platform === 'azure' && (
        <>
          {sectionLabel('Microsoft Entra')}
          <TextField size="small" fullWidth label="Azure Metadata URL" value={formData.azure_metadata_url || formData.metadata_url || ''}
            onChange={(e) => handleFormChange('azure_metadata_url', e.target.value)}
            placeholder="https://login.microsoftonline.com/.../FederationMetadata.xml" />
        </>
      )}
      {(formData.x509_certificate || formData.idp_initiated_url) && !importedIdpMeta && (
        <>
          {sectionLabel('IdP details')}
          {formData.idp_initiated_url && (
            <TextField size="small" fullWidth label="IdP SSO / Initiated URL" value={formData.idp_initiated_url}
              onChange={(e) => handleFormChange('idp_initiated_url', e.target.value)} />
          )}
          {formData.x509_certificate && (
            <TextField size="small" fullWidth multiline minRows={3} label="X.509 Certificate" value={formData.x509_certificate}
              onChange={(e) => handleFormChange('x509_certificate', e.target.value)}
              InputProps={{ sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.72rem' } }} />
          )}
        </>
      )}
    </Box>
  );

  const renderOIDCTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {sectionLabel('OIDC Configuration')}
      <FormControl size="small" fullWidth required error={!!errors.grant_type}>
        <InputLabel>Grant Type</InputLabel>
        <Select value={formData.grant_type || ''} onChange={(e) => handleFormChange('grant_type', e.target.value)} label="Grant Type" MenuProps={MENU_PROPS}>
          <MenuItem value="authorization_code_pkce">Authorization Code with PKCE</MenuItem>
          <MenuItem value="authorization_code">Authorization Code</MenuItem>
          <MenuItem value="client_credentials">Client Credentials</MenuItem>
        </Select>
        {errors.grant_type && <Typography variant="caption" color="error">{errors.grant_type}</Typography>}
      </FormControl>
      {(formData.grant_type === 'authorization_code' || formData.grant_type === 'authorization_code_pkce') && (
        <TextField size="small" fullWidth required label="Redirect URIs" value={formData.redirect_uris?.join(', ') || ''}
          onChange={(e) => handleFormChange('redirect_uris', e.target.value.split(',').map(u => u.trim()))}
          placeholder={FORM_PLACEHOLDERS.oidcRedirectUris}
          helperText={errors.redirect_uris || undefined} error={!!errors.redirect_uris} />
      )}
      {formData.grant_type === 'client_credentials' && (
        <Alert severity="info" icon={false} sx={{ py: 0.75, fontSize: UI_TOKENS.text.buttonSm, '& .MuiAlert-message': { py: 0 } }}>
          Client Credentials is machine-to-machine (API) auth — no browser login, so a Redirect URI is not used.
          {!String(formData.client_id || '').trim()
            ? ' Submit for SSO approval and the SSO team will create the Entra App Registration (client ID + secret) after they approve.'
            : ''}
        </Alert>
      )}
      <TextField size="small" fullWidth label="Scopes" value={formData.scopes?.join(', ') || ''}
        onChange={(e) => handleFormChange('scopes', e.target.value.split(',').map(s => s.trim()))}
        placeholder={
          formData.grant_type === 'client_credentials'
            ? 'api://your-app/.default, or custom API scopes'
            : 'openid, profile, email, offline_access'
        }
        helperText={errors.scopes || undefined} error={!!errors.scopes} />

      {formData.platform === 'google' && (
        <>
          {sectionLabel('Google Workspace')}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField size="small" fullWidth label="Google Project ID" value={formData.google_project_id || ''}
              onChange={(e) => handleFormChange('google_project_id', e.target.value)}
              placeholder="your-project-id-123456" error={!!errors.google_project_id} />
            <TextField size="small" fullWidth label="Google Client ID" value={formData.google_client_id || ''}
              onChange={(e) => handleFormChange('google_client_id', e.target.value)}
              placeholder="123456789-abcdefg.apps.googleusercontent.com" error={!!errors.google_client_id} />
          </Box>
          <FormControlLabel sx={{ '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm } }}
            control={<Switch size="small" checked={formData.google_consent_screen_configured || false}
              onChange={(e) => handleFormChange('google_consent_screen_configured', e.target.checked)} />}
            label="Consent Screen Configured" />
        </>
      )}
      {formData.platform === 'generic_saml' && (
        <>
          {sectionLabel('Generic SAML Provider')}
          <TextField size="small" fullWidth label="Metadata URL" value={formData.metadata_url || ''}
            onChange={(e) => handleFormChange('metadata_url', e.target.value)}
            placeholder={FORM_PLACEHOLDERS.samlMetadataUrl} error={!!errors.metadata_url} />
        </>
      )}
      {formData.platform === 'generic_oidc' && (
        <>
          {sectionLabel('Generic OIDC Provider')}
          <TextField size="small" fullWidth label="OIDC issuer or discovery URL" value={formData.oidc_discovery_endpoint || ''}
            onChange={(e) => handleFormChange('oidc_discovery_endpoint', e.target.value)}
            placeholder={FORM_PLACEHOLDERS.oidcDiscoveryUrl} error={!!errors.oidc_discovery_endpoint} />
        </>
      )}
      <Alert severity="info" icon={false} sx={{ py: 0.75, fontSize: UI_TOKENS.text.buttonSm, '& .MuiAlert-message': { py: 0 } }}>
        After approval,{' '}
        <BrandedWordmark
          name="PROVYSN.ai"
          variant="body2"
          component="span"
          sx={{ display: 'inline-flex', verticalAlign: 'baseline', fontSize: 'inherit' }}
        />{' '}
        will provide your <strong>Client ID</strong>
        {formData.grant_type !== 'authorization_code_pkce' && <>, <strong>Client Secret</strong></>} and <strong>OIDC Provider URL</strong>.
      </Alert>
    </Box>
  );

  const sectionLabel = (text: string) => (
    <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, fontWeight: 700, letterSpacing: '0.08em', color: 'text.disabled', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
      {text}
    </Typography>
  );

  const riskSurface = riskLevel ? getRiskSurface(riskLevel, muiTheme.palette.mode) : null;
  const riskKey = riskLevel || '';
  const riskColors: Record<string, string> = riskSurface
    ? { [riskKey]: riskSurface.fg }
    : {};
  const riskBg: Record<string, string> = riskSurface
    ? { [riskKey]: riskSurface.bg }
    : {};

  // Menus render in a Portal outside the Dialog PaperProps, so font must be set via MenuProps
  const MENU_PROPS = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: UI_TOKENS.text.bodySm,
          minHeight: UI_TOKENS.menu.itemMinHeight,
          py: UI_TOKENS.menu.itemPaddingY,
        },
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh',
        '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
        '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
        '& .MuiMenuItem-root': { fontSize: UI_TOKENS.text.bodySm },
        '& .MuiFormHelperText-root': { fontSize: UI_TOKENS.text.helper, mt: 0.25 },
        '& .MuiFormControlLabel-label': { fontSize: UI_TOKENS.text.bodySm },
        '& .MuiTypography-body2': { fontSize: UI_TOKENS.text.bodySm },
        '& .MuiTypography-caption': { fontSize: UI_TOKENS.text.caption },
        '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
        '& .MuiCheckbox-root': { padding: '2px 6px' },
      } }}>

      {/* Compact header */}
      <Box sx={{ background: `linear-gradient(135deg, ${muiTheme.palette.primary.dark} 0%, ${muiTheme.palette.primary.main} 100%)`, px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Settings sx={{ fontSize: 17, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1 }}>SSO Onboarding</Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{applicationName}</Typography>
          </Box>
          {riskLevel && riskColors[riskLevel] && (
            <Chip label={riskLevel} size="small" sx={{ ml: 1, fontSize: '0.68rem', fontWeight: 700, backgroundColor: riskBg[riskLevel], color: riskColors[riskLevel], height: 20 }} />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth"
        sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40,
          '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', py: 0 },
          '& .MuiTab-iconWrapper': { mr: 0.5 },
        }}>
        <Tab icon={<SmartToy sx={{ fontSize: 15 }} />} label="AI Assist" iconPosition="start" />
        <Tab icon={<Settings sx={{ fontSize: 15 }} />} label="Manual" iconPosition="start" />
        <Tab icon={<Upload sx={{ fontSize: 15 }} />} label="Upload" iconPosition="start" />
        <Tab icon={<People sx={{ fontSize: 15 }} />} label="Delegate" iconPosition="start" />
      </Tabs>

      <DialogContent sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
        {error && <Alert severity="error" sx={{ mx: 2, mt: 1.5, py: 0.5, fontSize: '0.78rem' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mx: 2, mt: 1.5, py: 0.5, fontSize: '0.78rem' }}>{success}</Alert>}
        {open && (!applicationId?.trim() || !instanceId?.trim()) && (
          <Alert severity="warning" sx={{ mx: 2, mt: 1.5, py: 0.75, fontSize: '0.78rem' }}>
            This form needs an <strong>application</strong> and <strong>instance</strong> from{' '}
            <BrandedWordmark name="PROVYSN.ai" variant="body2" component="span" sx={{ display: 'inline-flex', verticalAlign: 'baseline', fontSize: 'inherit' }} />.
            Ask VYSOR again which app to configure
            (it should use the IDs shown in your &quot;not yet SSO onboarded&quot; list), or open <strong>Applications</strong> and start SSO from there.
          </Alert>
        )}
        {(errors.application_context || errors.instance_context) && (
          <Alert severity="error" sx={{ mx: 2, mt: 1, py: 0.75, fontSize: '0.78rem' }}>
            {[errors.application_context, errors.instance_context].filter(Boolean).join(' ')}
          </Alert>
        )}

        {/* AI Assistant Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ height: 340, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, mb: 1.5, backgroundColor: 'action.hover' }}>
            {aiMessages.map((message, index) => (
              <Box key={index} sx={{ mb: 1.5, textAlign: message.role === 'user' ? 'right' : 'left' }}>
                <Paper elevation={0} sx={{ p: 1.5, display: 'inline-block', maxWidth: '80%', borderRadius: 2,
                  backgroundColor: message.role === 'user' ? muiTheme.palette.primary.main : 'background.paper',
                  border: message.role === 'user' ? 'none' : '1px solid',
                  borderColor: message.role === 'user' ? 'transparent' : 'divider',
                  color: message.role === 'user' ? 'white' : 'text.primary' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>{message.content}</Typography>
                </Paper>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField fullWidth size="small" placeholder="Type your message or paste a metadata URL…" value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAISubmit()}
              disabled={aiLoading} />
            <Button variant="contained" size="small" onClick={handleAISubmit}
              disabled={!aiInput.trim() || aiLoading}
              startIcon={aiLoading ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <Send sx={{ fontSize: 14 }} />}>
              {aiLoading ? 'Sending…' : 'Send'}
            </Button>
          </Box>
        </TabPanel>

        {/* Manual Configuration Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* SSO Type + IdP + Basic Info */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <FormControl size="small" fullWidth required>
                <InputLabel>Identity Provider</InputLabel>
                <Select
                  value={formData.platform || 'azure'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFormChange('platform', value);
                    try {
                      localStorage.setItem('turboiam-idp-platform', value);
                    } catch {
                      /* ignore */
                    }
                  }}
                  label="Identity Provider"
                  MenuProps={MENU_PROPS}
                >
                  <MenuItem value="azure">Microsoft Entra ID (Azure)</MenuItem>
                  <MenuItem value="okta">Okta</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth required>
                <InputLabel>SSO Type</InputLabel>
                <Select value={formData.sso_type} onChange={(e) => handleFormChange('sso_type', e.target.value)} label="SSO Type" MenuProps={MENU_PROPS}>
                  <MenuItem value="SAML">SAML 2.0</MenuItem>
                  <MenuItem value="OIDC">OpenID Connect (OIDC)</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" fullWidth required label="Connection Name" value={formData.connection_name}
                onChange={(e) => handleConnectionNameChange(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" sx={{ color: 'text.secondary' }}>sso-</Typography></InputAdornment> }} />
              <TextField size="small" fullWidth required label="Contact Email" type="email" value={formData.contact_email}
                onChange={(e) => handleFormChange('contact_email', e.target.value)} />
            </Box>
            <Divider />

            {/* SAML / OIDC fields */}
            {formData.sso_type === 'SAML' ? renderSAMLTab() : renderOIDCTab()}

            <Divider />

            {/* Security Policy */}
            {riskLevel && ['Low', 'Medium', 'High', 'Critical'].includes(riskLevel) && (
              <Box>
                {sectionLabel('Security Policy — auto-applied on approval')}
                {loadingPolicy ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading...</Typography>
                  </Box>
                ) : riskPolicy ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: 1.5,
                    border: `1px solid ${riskSurface?.border || 'divider'}`, backgroundColor: riskBg[riskLevel] }}>
                    <Chip label={riskLevel} size="small" sx={{ fontSize: '0.65rem', fontWeight: 700, height: 18,
                      backgroundColor: riskBg[riskLevel], color: riskColors[riskLevel], border: `1px solid ${riskSurface?.border}` }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', color: riskColors[riskLevel] }}>
                      {riskPolicy.policy_name}
                    </Typography>
                    {riskPolicy.policy_type && (
                      <Typography variant="caption" color="text.secondary">({riskPolicy.policy_type.replace(/_/g, ' ')})</Typography>
                    )}
                  </Box>
                ) : policyLookupCode === 'POLICY_LOOKUP_ERROR' ? (
                  <Alert severity="error" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                    {policyLookupMessage || 'Could not look up the security policy.'}
                  </Alert>
                ) : policyLookupCode === 'POLICY_INACTIVE' ? (
                  <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                    An active policy is not configured for <strong>{riskLevel}-risk</strong> apps. Ask GRC admin to activate one at <em>Risk Policies</em>.
                  </Alert>
                ) : policyLookupCode === 'RISK_LEVEL_MISSING' || policyLookupCode === 'RISK_LEVEL_INVALID' ? (
                  <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                    {policyLookupMessage || 'Application risk classification is missing or invalid.'}
                  </Alert>
                ) : policyLookupCode === 'POLICY_NOT_FOUND' ? (
                  <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                    No policy configured for <strong>{riskLevel}-risk</strong> apps. Ask GRC admin to set one at <em>Risk Policies</em>.
                  </Alert>
                ) : null}
              </Box>
            )}

            <Divider />

            {/* Attributes to Release */}
            <Box>
              {sectionLabel('Attributes to Release')}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Standard</Typography>
                  <FormGroup>
                    {availableAttributes.filter(a => a.is_standard && a.is_active).map(attr => (
                      <FormControlLabel key={attr.id} sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem' } }}
                        control={<Checkbox size="small" checked={selectedAttributes.includes(attr.id)}
                          onChange={(e) => handleAttributeChange(attr.id, e.target.checked)} sx={{ py: 0.25 }} />}
                        label={attr.display_name} />
                    ))}
                  </FormGroup>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Custom</Typography>
                  <FormGroup>
                    {availableAttributes.filter(a => !a.is_standard && a.is_active).map(attr => (
                      <FormControlLabel key={attr.id} sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem' } }}
                        control={<Checkbox size="small" checked={selectedAttributes.includes(attr.id)}
                          onChange={(e) => handleAttributeChange(attr.id, e.target.checked)} sx={{ py: 0.25 }} />}
                        label={attr.display_name} />
                    ))}
                  </FormGroup>
                </Box>
              </Box>
            </Box>

            {/* Okta SAML attributes — pulled from Okta API (read-only). */}
            {formData.sso_type === 'SAML' && formData.platform === 'okta' && (
              <>
                <Divider />
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, gap: 1, flexWrap: 'wrap' }}>
                    <Box>
                      {sectionLabel('Okta SAML attribute statements')}
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: -0.5 }}>
                        Loaded directly from your Okta org{oktaAttributesSource ? ` (${oktaAttributesSource.replace(/_/g, ' ')})` : ''}.
                        These are read-only and will be applied when the SAML app is provisioned on approval.
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={loadingOktaAttributes}
                      startIcon={
                        loadingOktaAttributes ? (
                          <CircularProgress size={14} />
                        ) : (
                          <CloudDownloadIcon sx={{ fontSize: '0.95rem !important' }} />
                        )
                      }
                      onClick={() => pullOktaSamlAttributes(formData.okta_app_id)}
                      sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}
                    >
                      Refresh from Okta
                    </Button>
                  </Box>

                  {loadingOktaAttributes && attributeStatements.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        Pulling SAML attributes from Okta…
                      </Typography>
                    </Box>
                  ) : attributeStatements.length === 0 ? (
                    <Alert severity="info" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      No SAML attributes returned from Okta. Configure Okta under <strong>Security</strong>, then click
                      <strong> Refresh from Okta</strong>. Optionally enter an <strong>Okta App ID</strong> above to read
                      mappings from a specific SAML application.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {attributeStatements.map((stmt, idx) => (
                        <TextField
                          key={`${stmt.name}-${idx}`}
                          size="small"
                          fullWidth
                          label={`${stmt.name} (${stmt.type || 'EXPRESSION'})`}
                          value={formatAttributeStatementLine(stmt)}
                          InputProps={{ readOnly: true }}
                          inputProps={{
                            style: {
                              fontSize: '0.82rem',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </TabPanel>

        {/* Upload Metadata Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sectionLabel('Upload SAML Metadata XML')}
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
              Upload your metadata XML or provide a URL — configuration details will be extracted automatically.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <input accept=".xml" style={{ display: 'none' }} id="metadata-file-upload" type="file" onChange={handleFileUpload} />
              <label htmlFor="metadata-file-upload">
                <Button variant="outlined" size="small" component="span" startIcon={<Upload sx={{ fontSize: 14 }} />} disabled={loading}>
                  Choose XML File
                </Button>
              </label>
              {selectedFile && <Chip label={selectedFile.name} size="small" onDelete={() => setSelectedFile(null)} />}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" label="Metadata URL" value={metadataUrl}
                onChange={(e) => setMetadataUrl(e.target.value)} placeholder={FORM_PLACEHOLDERS.metadataXmlUrl} />
              <Button variant="contained" size="small" onClick={handleFetchMetadataFromURL}
                disabled={fetchingMetadata || !metadataUrl.trim()}
                startIcon={fetchingMetadata ? <CircularProgress size={14} /> : undefined}
                sx={{ minWidth: 90 }}>
                {fetchingMetadata ? 'Fetching...' : 'Fetch'}
              </Button>
            </Box>

            {uploadedMetadata && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Uploaded Metadata Preview:</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {uploadedMetadata}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Delegation Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sectionLabel('Delegate To')}
            <FormControlLabel
              sx={{ mt: -1, '& .MuiFormControlLabel-label': { fontSize: '0.82rem' } }}
              control={<Switch size="small" checked={showDelegation} onChange={(e) => setShowDelegation(e.target.checked)} />}
              label="Delegate this onboarding to another user"
            />
            {showDelegation && (
              <FormControl size="small" fullWidth error={!!errors.delegated_to}>
                <InputLabel>Delegate To</InputLabel>
                <Select value={formData.delegated_to || ''} onChange={(e) => handleFormChange('delegated_to', e.target.value)}
                  label="Delegate To" disabled={loadingUsers} MenuProps={MENU_PROPS}>
                  {availableUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Box sx={{ lineHeight: 1.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.delegated_to && <Typography variant="caption" color="error">{errors.delegated_to}</Typography>}
              </FormControl>
            )}

            <Divider />
            {sectionLabel('Business Context')}
            <TextField size="small" fullWidth multiline rows={2} label="Business Justification"
              value={formData.business_justification || ''} onChange={(e) => handleFormChange('business_justification', e.target.value)}
              placeholder="Why is this SSO integration needed?" />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField size="small" fullWidth type="number" label="Expected Users"
                value={formData.expected_user_count || ''} onChange={(e) => handleFormChange('expected_user_count', parseInt(e.target.value) || 0)}
                placeholder="100" />
              <FormControl size="small" fullWidth>
                <InputLabel>Data Classification</InputLabel>
                <Select value={formData.data_classification || 'internal'} onChange={(e) => handleFormChange('data_classification', e.target.value)} label="Data Classification" MenuProps={MENU_PROPS}>
                  {dataClassifications.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      <Box sx={{ lineHeight: 1.2 }}>
                        <Typography variant="body2">{c.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />
            {sectionLabel('Technical Notes')}
            <TextField size="small" fullWidth multiline rows={2} label="Technical Instructions"
              value={formData.technical_notes || ''} onChange={(e) => handleFormChange('technical_notes', e.target.value)}
              placeholder="Technical requirements or constraints for the delegated user..." />
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Button size="small" onClick={onClose} sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSubmit}
          disabled={loading || !formData.connection_name || !formData.contact_email}
          startIcon={loading ? <CircularProgress size={13} /> : undefined}
          sx={{ background: `linear-gradient(135deg, ${muiTheme.palette.primary.dark}, ${muiTheme.palette.primary.main})`, px: 2.5, fontSize: '0.78rem' }}>
          {loading ? 'Submitting…' : 'Submit SSO Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SSOOnboardingDialog;
